import Lenis from "lenis";

/**
 * Handles smooth scrolling helpers.
 *
 * @package AGDS
 * @category Modules
 * @module Scroll
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class Scroll {
    /**
     * Creates a scroll helper instance.
     *
     * @param {Object} options
     * @param {HTMLElement} [options.header]
     */
    constructor(options = {}) {
        this.header = options.header;

        this.lenis = null;
        this.raf = null;
        this.frameId = null;
    }

    /**
     * Initializes scroll features.
     *
     * @returns {void}
     */
    init() {
        this.initLenis();
        this.smoothScrollToAnchor();
        this.backToTopButton();
    }

    /**
     * Lenis scroll features.
     *
     * @returns {void}
     */
    initLenis() {
        this.lenisDestroy();

        this.lenis = new Lenis();
        window.agdsLenis = this.lenis;

        this.raf = (time) => {
            this.lenis?.raf(time * 1000);
        };

        const nativeRaf = (time) => {
            this.lenis?.raf(time);
            this.frameId = requestAnimationFrame(nativeRaf);
        };

        this.frameId = requestAnimationFrame(nativeRaf);
    }

    /**
     * Destroys Lenis scroll features.
     *
     * @returns {void}
     */
    lenisDestroy() {
        this.raf = null;

        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
            this.frameId = null;
        }

        if (window.agdsLenis === this.lenis) {
            window.agdsLenis = null;
        }

        this.lenis?.lenisDestroy();
        this.lenis = null;
    }

    /**
     * Smoothly scrolls anchor links and offsets by header height.
     *
     * @returns {void}
     */
    smoothScrollToAnchor() {
        const anchorLinks = document.querySelectorAll('a[href*="#"]:not([data-fancybox])');
        if (!anchorLinks.length) return;

        anchorLinks.forEach((anchor) => {
            anchor.addEventListener("click", (event) => {
                const href = anchor.getAttribute("href");
                const hash = href.split("#")[1];

                if (hash && document.getElementById(hash)) {
                    event.preventDefault();
                    const headerHeight = this.header ? this.header.offsetHeight : 0;
                    const targetOffset = document.getElementById(hash).offsetTop - headerHeight;
                    this.scrollToWindowTop(targetOffset);
                }
            });
        });
    }

    /**
     * Scrolls the window to the given offset.
     *
     * @param {number} top - Top offset.
     * @returns {void}
     */
    scrollToWindowTop(top = 0) {
        window.scrollTo({ top: top, behavior: "smooth" });
    }

    /**
     * Binds the back-to-top button.
     *
     * @returns {void}
     */
    backToTopButton() {
        const backToTopBtn = document.getElementById("backtotop-btn");
        if (!backToTopBtn) return;

        backToTopBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this.scrollToWindowTop();
        });
    }
}
